import { KLineData } from "klinecharts";

export function getPriceValue(kLineData: KLineData, applyTo: string): number {
  switch (applyTo) {
    case "Open":
      return kLineData.open;
    case "High":
      return kLineData.high;
    case "Low":
      return kLineData.low;
    case "Close":
      return kLineData.close;
    case "Median Price (HL/2)":
      return (kLineData.high + kLineData.low) / 2;
    case "Typical Price (HLC/3)":
      return (kLineData.high + kLineData.low + kLineData.close) / 3;
    case "Weighted Close (HLCC/4)":
      return (kLineData.high + kLineData.low + kLineData.close * 2) / 4;
    default:
      return kLineData.close;
  }
}

/** Monotonic deque for rolling max over a fixed window (stores indices). */
export class RollingMaxDeque {
  private readonly deque: number[] = [];

  push(index: number, value: number, values: number[], window: number): void {
    while (
      this.deque.length > 0 &&
      values[this.deque[this.deque.length - 1]!] <= value
    ) {
      this.deque.pop();
    }
    this.deque.push(index);
    while (this.deque.length > 0 && this.deque[0]! <= index - window) {
      this.deque.shift();
    }
  }

  max(values: number[]): number {
    return values[this.deque[0]!]!;
  }

  get front(): number {
    return this.deque[0]!;
  }
}

/** Monotonic deque for rolling min over a fixed window (stores indices). */
export class RollingMinDeque {
  private readonly deque: number[] = [];

  push(index: number, value: number, values: number[], window: number): void {
    while (
      this.deque.length > 0 &&
      values[this.deque[this.deque.length - 1]!] >= value
    ) {
      this.deque.pop();
    }
    this.deque.push(index);
    while (this.deque.length > 0 && this.deque[0]! <= index - window) {
      this.deque.shift();
    }
  }

  min(values: number[]): number {
    return values[this.deque[0]!]!;
  }
}

export function shiftResults<T extends Record<string, unknown>>(
  results: T[],
  shift: number
): T[] {
  const n = results.length;
  if (shift === 0) return results;
  const shifted: T[] = new Array(n);
  for (let i = 0; i < n; i++) shifted[i] = {} as T;
  for (let i = 0; i < n; i++) {
    const target = i + shift;
    if (target >= 0 && target < n) {
      shifted[target] = results[i]!;
    }
  }
  return shifted;
}

export function readStyle(
  styles: Record<string, { color?: string; style?: string }>,
  id: string,
  defaultColor: string,
  defaultStyle: "solid" | "dashed" | "dotted" = "solid"
): { color: string; style: "solid" | "dashed" | "dotted" } {
  const s = styles[id];
  return {
    color: s?.color ?? defaultColor,
    style: (s?.style as "solid" | "dashed" | "dotted") ?? defaultStyle,
  };
}
